import { NGXLogger } from 'ngx-logger';
import { Component, OnDestroy } from '@angular/core';
import { CheckDetailService } from '@services/checkDetail/check-detail.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { QuestionService } from '@modules/management/pages/details/check/services';
import { AccountsService, ChecksService, LoadingService, ViewerService, CheckResponseService } from '@services';
import { USER_QUESTION_TYPES } from '@modules/management/pages/details/check/models';
import { formatUserAnswer } from '@services/accounts/user-filter';
import { PopoverController } from '@ionic/angular';
import { Events } from '@services/events/events.service';
import { NotePopupComponent } from '@modules/observation/components';
import { DeploymentService } from '@modules/management/pages/details/check/services/deployment/deployment.service';

import {Location} from '@angular/common';
import { each, get, includes } from 'lodash';

@Component({
  selector: 'app-issue-detail',
  templateUrl: './issue-detail.page.html',
  styleUrls: ['./issue-detail.page.scss'],
})
export class IssueDetailPage implements OnDestroy {

  public headerData: any = {};
  public flagID: number;
  public flagObject: any = {};
  public isPositiveAnswer: boolean;
  public caDetail: any = {};
  public images: any = [];
  public notes: any = [];
  public footerData;
  private rid = '';
  private questionId;
  private responseObject: any;

  constructor(
    private checkDetailService: CheckDetailService,
    private logger: NGXLogger,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private questionService: QuestionService,
    private checksService: ChecksService,
    private checkResponseService: CheckResponseService,
    private popover: PopoverController,
    protected events: Events,
    private loadingService: LoadingService,
    private viewer: ViewerService,
    private router: Router,
    private deployment: DeploymentService,
    private location: Location,
    private accountsService: AccountsService
  ) {}

  ngOnDestroy() {
    this.events.unsubscribe('ccs:noteUpdate', this.noteListener);
  }

  async ionViewWillEnter() {
    this.flagID = +this.activatedRoute.snapshot.paramMap.get('fid');
    this.rid = this.activatedRoute.snapshot.paramMap.get('rid');
    this.questionId = this.activatedRoute.snapshot.paramMap.get('questionId');
    // subscribe before the awaits below, so an ionViewWillLeave that fires mid-load removes it
    this.events.subscribe('ccs:noteUpdate', this.noteListener);

    this.responseObject = await this.checkResponseService.getResponseAsync(this.rid);

    if (this.flagID) {
      this.headerData = await this.checkDetailService.getTopComponentData(this.rid, 'logged');
      await this.loadingService.enable();
      try {
        const testObj = await this.checkResponseService.getFlagIssue([this.flagID]);
        this.flagObject = testObj[this.flagID];
      } catch (error) {
        this.logger.log(error);
      }

      await this.prepareDataSet();
      this.loadingService.disable();
    } else if (this.questionId) {
      this.defineAnswer();
    }
    //   this.flagObject = this.checkDetailService.getIssueByIssueId(this.flagID);
  }

  ionViewWillLeave() {
    this.events.unsubscribe('ccs:noteUpdate', this.noteListener);
  }

  async prepareDataSet() {
    // get question and answer stuff
    const questionOptions = this.questionService.getQuestionOptions(this.flagObject.questionID);
    const questionObject = this.questionService.getQuestion(this.flagObject.questionID);
    const respValue = this.responseObject.answers[this.flagObject.questionID].response;
    if (includes(USER_QUESTION_TYPES, questionObject?.type)) {
      this.caDetail.answer = this.userAnswer(respValue);
    } else if (this.checkDetailService.isUUID(respValue)) {
      const questionOption = questionOptions[respValue];
      this.caDetail.answer = this.checkDetailService.translateData(questionOption);
    } else {
      let rString = respValue;
      if (respValue === 'pass') {
        rString = this.translate.instant('MGMT_DETAILS.Pass');
      } else if (respValue === 'fail') {
        rString = this.translate.instant('MGMT_DETAILS.Fail');
      }
      this.caDetail.answer = rString;
    }
    const checkData = this.checksService.getCheck(this.flagObject.checkID);
    this.headerData.headerElements.push(this.checkDetailService.type(checkData.checkType));
    this.headerData.headerElements.push(this.checkDetailService.targetFullColumn(this.flagObject));
    this.caDetail.question = this.checkDetailService.translateData(questionObject);
    this.caDetail.name = this.checkDetailService.translateData(checkData);
    const deploymentName = this.deployment.getDeployment(this.flagObject.deploymentID);
    this.caDetail.deployment = deploymentName ? deploymentName.title : '';
    this.images = this.checkDetailService.getImages(this.flagObject);
    this.notes = this.checkDetailService.getNotes(this.flagObject);

    if (!this.activatedRoute.snapshot.queryParamMap.get('footerIsHidden')) {
      this.footerData = await this.checkDetailService.getFooterComponent(this.rid, 'logged', this.flagID);
    }
  }

  // a user-question answer is a pid or pids, not an option ID; [] is N/A
  private userAnswer(response: unknown): string {
    return formatUserAnswer(response, (userID) => this.accountsService.getByID(userID),
      this.translate.instant('MGMT_DETAILS.User_Disabled'), this.translate.instant('MGMT_DETAILS.User_Unknown'))
      || this.translate.instant('DASHPAGES.NA');
  }

  private async defineAnswer() {
    const currentAnswer = this.responseObject.answers[this.questionId];
    this.isPositiveAnswer = currentAnswer.result === 'positive';
    currentAnswer.created = currentAnswer.startTime;
    currentAnswer.creator = currentAnswer.responder;
    each(currentAnswer.notes, (note) => {
      note.createdAt ??= currentAnswer.startTime;
      note.userID ??= currentAnswer.responder;
    });

    const questionOptions = this.questionService.getQuestionOptions(this.questionId);
    const questionObject = this.questionService.getQuestion(this.questionId);
    const asnwer = this.responseObject.answers[this.questionId];
    const respValue = asnwer?.response;

    this.headerData = await this.checkDetailService.getTopComponentData(this.rid, 'validation');

    if (includes(USER_QUESTION_TYPES, questionObject?.type)) {
      this.caDetail.answer = this.userAnswer(respValue);
    } else if (this.checkDetailService.isUUID(respValue)) {
      const questionOption = questionOptions[respValue];
      this.caDetail.answer = this.checkDetailService.translateData(questionOption);
    } else {
      let rString = respValue;
      if (respValue === 'pass' || asnwer?.result === 'positive') {
        rString = this.translate.instant('MGMT_DETAILS.Pass');
      } else if (respValue === 'fail' || asnwer?.result === 'negative') {
        rString = this.translate.instant('MGMT_DETAILS.Fail');
      }
      this.caDetail.answer = rString;
    }
    const checkData = this.checksService.getCheck(this.responseObject.checkID);
    this.caDetail.question = this.checkDetailService.translateData(questionObject);
    this.caDetail.name = this.checkDetailService.translateData(checkData);
    this.caDetail.deployment = this.deployment.getDeployment(this.responseObject.deploymentID)?.title || '';
    this.images = this.checkDetailService.getImages(currentAnswer);
    this.notes = this.checkDetailService.getNotes(currentAnswer);
  }

  public addOpenNote() {
    this.popover.create(<any>{
      component: NotePopupComponent,
      animated: false,
      componentProps: {
        flagID: this.flagID,
        msgType: 'text'
      }
    }).then((element: HTMLIonPopoverElement) => {
      element.present();
    });
  }

  back() {
    if (get(this.activatedRoute.snapshot.queryParams, 'backNavigation')) {
      this.location.back();
    } else {
      this.router.navigate([this.footerData.nav]);
    }
  }

  private noteListener = async (data) => {
    if (data) {
      try {
        const testObj = await this.checkResponseService.getFlagIssue([this.flagID]);
        this.flagObject = testObj[this.flagID];
        this.notes = this.checkDetailService.getNotes(this.flagObject);
      } catch (error) {
        this.logger.log(error);
      }
    }
  };

}

